import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import {
  ColDef,
  ICellRendererParams,
  themeQuartz,
  CellStyle,
} from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import {
  Bell,
  AlertTriangle,
  Clock,
  CheckCircle,
  Eye,
  Search,
  Filter,
} from "lucide-react";
import {
  message,
  Tooltip,
  Badge,
  Select,
  Modal,
  Divider,
  Tag,
  Button,
} from "antd";
import { useRoundNotificationStore } from "../../../store/RoundNotificationStore";
import { PageLoader } from "../../../components/loader/Loader";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgb(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.80rem",
  headerFontWeight: "bold",
});

const NotificationList: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Use Zustand store with selectors
  const notifications = useRoundNotificationStore(
    (state) => state.notifications,
  );
  const currentNotification = useRoundNotificationStore(
    (state) => state.currentNotification,
  );
  const stats = useRoundNotificationStore((state) => state.stats);
  const loading = useRoundNotificationStore((state) => state.loading);
  const unreadCount = useRoundNotificationStore((state) => state.unreadCount);
  const fetchNotifications = useRoundNotificationStore(
    (state) => state.fetchNotifications,
  );
  const fetchStats = useRoundNotificationStore((state) => state.fetchStats);
  const fetchNotificationById = useRoundNotificationStore(
    (state) => state.fetchNotificationById,
  );
  const clearCurrentNotification = useRoundNotificationStore(
    (state) => state.clearCurrentNotification,
  );
  const acknowledgeNotificationAction = useRoundNotificationStore(
    (state) => state.acknowledgeNotification,
  );
  const markAsReadAction = useRoundNotificationStore(
    (state) => state.markAsRead,
  );
  const markAllAsReadAction = useRoundNotificationStore(
    (state) => state.markAllAsRead,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState<string>("");
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);

  useEffect(() => {
    fetchNotifications();
    fetchStats();
  }, [fetchNotifications, fetchStats]);

  useEffect(() => {
    if (id) {
      fetchNotificationById(id);
      setIsDetailModalVisible(true);
    } else {
      setIsDetailModalVisible(false);
      clearCurrentNotification();
    }
  }, [id, fetchNotificationById, clearCurrentNotification]);

  const handleCloseModal = () => {
    setIsDetailModalVisible(false);
    clearCurrentNotification();
    navigate("/safety-facility-rounds/notifications");
  };

  const handleAcknowledge = async (notificationId: string) => {
    const success = await acknowledgeNotificationAction(notificationId);
    if (success) {
      message.success("Notification acknowledged");
      fetchNotifications();
    } else {
      message.error("Failed to acknowledge notification");
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    const success = await markAsReadAction(notificationId);
    if (success) {
      message.success("Marked as read");
    }
  };

  // Cell Renderers
  const EventTypeRenderer = (params: ICellRendererParams) => {
    const eventType = params.value;
    const config: Record<
      string,
      { icon: React.ReactNode; color: string; label: string }
    > = {
      critical_issue: {
        icon: <AlertTriangle size={14} />,
        color: "bg-red-100 text-red-800 border-red-200",
        label: "Critical Issue",
      },
      missed_round: {
        icon: <Clock size={14} />,
        color: "bg-orange-100 text-orange-800 border-orange-200",
        label: "Missed Round",
      },
      sla_breach: {
        icon: <Clock size={14} />,
        color: "bg-yellow-100 text-yellow-800 border-yellow-200",
        label: "SLA Breach",
      },
      escalation: {
        icon: <Bell size={14} />,
        color: "bg-purple-100 text-purple-800 border-purple-200",
        label: "Escalation",
      },
      repeat_noncompliance: {
        icon: <AlertTriangle size={14} />,
        color: "bg-pink-100 text-pink-800 border-pink-200",
        label: "Repeat Issue",
      },
    };
    const cfg = config[eventType] || {
      icon: <Bell size={14} />,
      color: "bg-gray-100 text-gray-800",
      label: eventType,
    };
    return (
      <span
        className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${cfg.color}`}
      >
        {cfg.icon}
        {cfg.label}
      </span>
    );
  };

  const PriorityRenderer = (params: ICellRendererParams) => {
    const priority = params.value;
    const colors: Record<string, string> = {
      critical: "bg-red-500 text-white",
      high: "bg-orange-500 text-white",
      medium: "bg-yellow-500 text-white",
      low: "bg-green-500 text-white",
    };
    return (
      <span
        className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
          colors[priority] || "bg-gray-200"
        }`}
      >
        {priority}
      </span>
    );
  };

  const StatusRenderer = (params: ICellRendererParams) => {
    const status = params.value;
    const colors: Record<string, string> = {
      pending: "bg-gray-100 text-gray-600",
      sent: "bg-blue-100 text-blue-800",
      delivered: "bg-green-100 text-green-800",
      acknowledged: "bg-blue-100 text-blue-800 border-blue-200",
      partially_delivered: "bg-yellow-100 text-yellow-800",
      failed: "bg-red-100 text-red-800",
    };
    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          colors[status] || "bg-gray-100"
        }`}
      >
        {status?.replace("_", " ")}
      </span>
    );
  };

  const DeliveryStatsRenderer = (params: ICellRendererParams) => {
    const stats = params.value;
    if (!stats) return <span className="text-gray-400">-</span>;
    return (
      <div className="flex items-center gap-2 text-xs">
        <Tooltip title="Delivered">
          <span className="text-green-600">
            {stats.delivered || 0}/{stats.total || 0}
          </span>
        </Tooltip>
        <Tooltip title="Acknowledged">
          <span className="text-blue-600 flex items-center gap-0.5">
            <CheckCircle size={12} />
            {stats.acknowledged || 0}
          </span>
        </Tooltip>
      </div>
    );
  };

  const ActionsRenderer = (params: ICellRendererParams) => {
    const notification = params.data;
    return (
      <div className="flex items-center gap-2">
        <Tooltip title="View Details">
          <button
            onClick={() =>
              navigate(
                `/safety-facility-rounds/notifications/${notification._id}`,
              )
            }
            className="p-1.5 hover:bg-gray-100 rounded-full text-blue-600 transition-colors"
          >
            <Eye size={16} />
          </button>
        </Tooltip>
        {notification.status !== "acknowledged" && (
          <Tooltip title="Acknowledge">
            <button
              onClick={() => handleAcknowledge(notification._id)}
              className="p-1.5 hover:bg-green-100 rounded-full text-green-600 transition-colors"
            >
              <CheckCircle size={16} />
            </button>
          </Tooltip>
        )}
      </div>
    );
  };

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        field: "eventType",
        headerName: "Event Type",
        minWidth: 160,
        cellRenderer: EventTypeRenderer,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
        minWidth: 200,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "priority",
        headerName: "Priority",
        width: 100,
        cellRenderer: PriorityRenderer,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "escalationLevel",
        headerName: "Level",
        width: 80,
        valueFormatter: (params) => `L${params.value || 1}`,
        cellStyle: { padding: "10px", textAlign: "center" } as CellStyle,
      },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        cellRenderer: StatusRenderer,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "deliveryStats",
        headerName: "Delivery",
        width: 120,
        cellRenderer: DeliveryStatsRenderer,
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "createdAt",
        headerName: "Created",
        width: 150,
        valueFormatter: (params) =>
          params.value ? new Date(params.value).toLocaleString() : "-",
        cellStyle: { padding: "10px" } as CellStyle,
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 100,
        pinned: "right",
        cellRenderer: ActionsRenderer,
        sortable: false,
        filter: false,
        cellStyle: { padding: "10px" } as CellStyle,
      },
    ],
    [],
  );

  const filteredNotifications = useMemo(() => {
    let filtered = notifications;
    if (searchTerm) {
      filtered = filtered.filter(
        (n) =>
          n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          n.message.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (eventTypeFilter) {
      filtered = filtered.filter((n) => n.eventType === eventTypeFilter);
    }
    if (priorityFilter) {
      filtered = filtered.filter((n) => n.priority === priorityFilter);
    }
    return filtered;
  }, [notifications, searchTerm, eventTypeFilter, priorityFilter]);

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
    }),
    [],
  );

  if (loading && !notifications.length) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  return (
    <div className="p-2 mt-20 h-full  max-w-[1600px] mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl text-gray-900">Round Notifications</h1>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge count={unreadCount} offset={[-5, 5]}>
            <button
              onClick={() => markAllAsReadAction()}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Mark All Read
            </button>
          </Badge>
          <button
            onClick={() => navigate("/safety-facility-rounds/escalation-rules")}
            className="px-4 py-2 bg-[#0fa9a6] text-white rounded-lg hover:bg-[#197f7d] transition-colors text-sm font-medium"
          >
            Manage Rules
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Unread</p>
            <p className="text-2xl font-bold text-purple-600 mt-1">
              {stats.unreadCount}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Last 24h</p>
            <p className="text-2xl font-bold text-blue-600 mt-1">
              {stats.recentCount}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">Critical Issues</p>
            <p className="text-2xl font-bold text-red-600 mt-1">
              {stats.byEventType?.critical_issue || 0}
            </p>
          </div>
          <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <p className="text-sm text-gray-500 font-medium">SLA Breaches</p>
            <p className="text-2xl font-bold text-yellow-600 mt-1">
              {stats.byEventType?.sla_breach || 0}
            </p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row gap-4 bg-gray-50/50">
          <div className="relative flex-1 max-w-md">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search notifications..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
            />
          </div>
          <div className="flex gap-2">
            <Select
              placeholder="Event Type"
              allowClear
              style={{ width: 150 }}
              value={eventTypeFilter || undefined}
              onChange={(val) => setEventTypeFilter(val || "")}
              options={[
                { value: "critical_issue", label: "Critical Issue" },
                { value: "missed_round", label: "Missed Round" },
                { value: "sla_breach", label: "SLA Breach" },
                { value: "escalation", label: "Escalation" },
                { value: "repeat_noncompliance", label: "Repeat Non-Compliance" },
              ]}
            />
            <Select
              placeholder="Priority"
              allowClear
              style={{ width: 120 }}
              value={priorityFilter || undefined}
              onChange={(val) => setPriorityFilter(val || "")}
              options={[
                { value: "critical", label: "Critical" },
                { value: "high", label: "High" },
                { value: "medium", label: "Medium" },
                { value: "low", label: "Low" },
              ]}
            />
          </div>
        </div>

        {/* Grid */}
        <div className="ag-theme-alpine w-full" style={{ height: "550px" }}>
          <AgGridReact
            rowData={filteredNotifications}
            columnDefs={columnDefs}
            theme={gridTheme}
            defaultColDef={defaultColDef}
            pagination={true}
            paginationPageSize={10}
            animateRows={true}
            rowHeight={55}
            headerHeight={55}
            suppressCellFocus={true}
            enableCellTextSelection={true}
          />
        </div>
      </div>

      {/* Notification Detail Modal */}
      <Modal
        title={
          <div className="flex items-center gap-2">
            <Bell size={18} className="text-purple-600" />
            <span>Notification Details</span>
          </div>
        }
        open={isDetailModalVisible}
        onCancel={handleCloseModal}
        zIndex={1050}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Close
          </Button>,
          currentNotification?.status !== "acknowledged" && (
            <Button
              key="ack"
              type="primary"
              className="bg-green-600 hover:bg-green-700"
              onClick={() => {
                if (currentNotification) {
                  handleAcknowledge(currentNotification._id);
                  handleCloseModal();
                }
              }}
            >
              Acknowledge
            </Button>
          ),
        ]}
        width={700}
        centered
        destroyOnClose
      >
        {loading && !currentNotification ? (
          <div className="py-20 flex justify-center">
            <PageLoader />
          </div>
        ) : currentNotification ? (
          <div className="space-y-6">
            <div>
              <div className="flex justify-between items-start mb-2">
                <h2 className="text-xl font-bold text-gray-900">
                  {currentNotification.title}
                </h2>
                <Tag
                  color={
                    currentNotification.priority === "critical"
                      ? "red"
                      : currentNotification.priority === "high"
                        ? "orange"
                        : "blue"
                  }
                  className="uppercase font-bold"
                >
                  {currentNotification.priority}
                </Tag>
              </div>
              <p className="text-gray-600 text-lg leading-relaxed">
                {currentNotification.message}
              </p>
            </div>

            <Divider className="my-4" />

            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">
                  Event Type
                </p>
                <Tag color="purple">
                  {currentNotification.eventType?.replace("_", " ")}
                </Tag>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">Status</p>
                <Tag
                  color={
                    currentNotification.status === "sent"
                      ? "blue"
                      : currentNotification.status === "delivered" ||
                          currentNotification.status === "acknowledged"
                        ? "green"
                        : "gray"
                  }
                >
                  {currentNotification.status?.replace("_", " ")}
                </Tag>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">
                  Created At
                </p>
                <div className="text-gray-900 flex items-center gap-2">
                  <Clock size={16} className="text-gray-400" />
                  {new Date(currentNotification.createdAt).toLocaleString()}
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-500 font-medium mb-1">
                  Notification Code
                </p>
                <div className="text-gray-900 font-mono">
                  {currentNotification.notificationCode || currentNotification.notificationNumber || "-"}
                </div>
              </div>
            </div>

            {currentNotification.recipients &&
              currentNotification.recipients.length > 0 && (
                <>
                  <Divider className="my-4" />
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">
                      Recipients & Status
                    </h3>
                    <div className="bg-gray-50 rounded-lg overflow-hidden border border-gray-100">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100 text-gray-600 text-left">
                          <tr>
                            <th className="px-4 py-2 font-semibold">User</th>
                            <th className="px-4 py-2 font-semibold">Role</th>
                            <th className="px-4 py-2 font-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {currentNotification.recipients.map(
                            (recipient, idx) => (
                              <tr key={idx} className="hover:bg-gray-50/50">
                                <td className="px-4 py-2 text-gray-900">
                                  {recipient.userName || recipient.userId}
                                </td>
                                <td className="px-4 py-2 text-gray-600">
                                  {recipient.role}
                                </td>
                                <td className="px-4 py-2">
                                  <Tag
                                    color={
                                      recipient.status === "delivered" ||
                                      recipient.status === "read" ||
                                      recipient.status === "acknowledged"
                                        ? "green"
                                        : "gray"
                                    }
                                    className="m-0 text-[10px]"
                                  >
                                    {recipient.status}
                                  </Tag>
                                </td>
                              </tr>
                            ),
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

            {(currentNotification.issueId ||
              currentNotification.roundExecutionId) && (
              <>
                <Divider className="my-4" />
                <div className="flex gap-3">
                  {currentNotification.issueId && (
                    <Button
                      type="default"
                      icon={<Eye size={16} />}
                      onClick={() =>
                        navigate(
                          `/safety-facility-rounds/issues/${currentNotification?.issueId}`,
                        )
                      }
                    >
                      View Linked Issue
                    </Button>
                  )}
                  {currentNotification.roundExecutionId && (
                    <Button
                      type="default"
                      icon={<Eye size={16} />}
                      onClick={() =>
                        navigate(
                          `/safety-facility-rounds/checklists/perform/${currentNotification?.roundExecutionId}`,
                        )
                      }
                    >
                      View Round Execution
                    </Button>
                  )}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="py-10 text-center text-gray-500">
            Notification not found.
          </div>
        )}
      </Modal>
    </div>
  );
};

export default NotificationList;
